import React, { useState, useEffect, useCallback } from 'react';
import './MasonryGallery.css';

// 画像をインポート
const importImage = (imageName: string) => {
  // publicフォルダの画像を参照
  const publicPath = `${process.env.PUBLIC_URL}/img/${imageName}`;
  console.log(`画像パス: ${publicPath}`);
  return publicPath;
};

interface ImageFile {
  name: string;
  title: string;
}

interface ImageData {
  id: number;
  url: string;
  title: string;
  category: string;
  rating: string;
  price: number;
}

interface MasonryGalleryProps {
  className?: string;
}

const MasonryGallery: React.FC<MasonryGalleryProps> = ({ className = '' }) => {
  const [images, setImages] = useState<ImageData[]>([]);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [activeSort, setActiveSort] = useState<string>('relevance');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // front/src/img/配下のローカル画像データ
  const imageFiles: ImageFile[] = [
    { name: 'QRCode.png', title: 'QRコード' },
    { name: 'back_button.png', title: 'バックボタン' },
    { name: 'back_ground2.png', title: '背景2' },
    { name: 'background_screen.png', title: '背景スクリーン' },
    { name: 'failed_mark.png', title: '失敗マーク' },
    { name: 'eleganttemplate.png', title: 'エレガントテンプレート' },
    { name: 'photo_selection_1.png', title: '写真選択1' },
    { name: 'photo_selection_2.png', title: '写真選択2' },
    { name: 'romantictemplate.png', title: 'ロマンチックテンプレート' },
    { name: 'screen.png', title: 'スクリーン' },
    { name: 'success_mark.png', title: '成功マーク' },
    { name: 'select_photo_button.png', title: '写真選択ボタン' },
    { name: 'swipe_hand.png', title: 'スワイプハンド' },
    { name: 'upload.png', title: 'アップロード' },
    { name: 'view_all_button.png', title: '全て表示ボタン' },
    { name: 'wedding1.jpg', title: '結婚式写真1' },
    { name: 'wedding2.jpg', title: '結婚式写真2' },
    { name: 'wedding3.jpg', title: '結婚式写真3' },
    { name: 'wedding4.jpg', title: '結婚式写真4' },
    { name: 'welcometemplate.png', title: 'ウェルカムテンプレート' }
  ];

  const categories = ['people', 'nature', 'animals', 'city', 'food', 'tech'];

  // 画像データを生成
  const generateImageData = useCallback((): ImageData[] => {
    return imageFiles.map((imageFile, i) => {
      const category = categories[Math.floor(Math.random() * categories.length)];
      const rating = (Math.random() * 2 + 3).toFixed(1);
      const price = Math.floor(Math.random() * 5000) + 500;
      
      return {
        id: i + 1,
        url: importImage(imageFile.name),
        title: imageFile.title,
        category,
        rating,
        price
      };
    });
  }, []); // 依存配列を空にして無限ループを防ぐ

  // 画像を読み込む
  const loadImages = useCallback(async () => {
    if (images.length > 0) return; // 既に画像がある場合は何もしない
    
    setIsLoading(true);
    
    try {
      // スケルトン表示のための疑似遅延
      await new Promise(resolve => setTimeout(resolve, 500));
      const imageData = generateImageData();
      console.log('生成された画像データ:', imageData.slice(0, 3)); // 最初の3件を表示
      setImages(imageData);
    } catch (error) {
      console.error('画像の読み込みに失敗しました:', error);
    } finally {
      setIsLoading(false);
    }
  }, [generateImageData, images.length]); // images.lengthを依存配列に追加

  // 初期読み込み
  useEffect(() => {
    if (images.length === 0 && !isLoading) {
      loadImages();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // フィルター処理
  const handleFilterChange = (filter: string) => {
    setActiveFilter(filter);
    // ダミー動作：実際のフィルタリングロジックをここに実装
    console.log(`フィルター変更: ${filter}`);
  };

  // ソート処理
  const handleSortChange = (sort: string) => {
    setActiveSort(sort);
    // ダミー動作：実際のソートロジックをここに実装
    console.log(`ソート変更: ${sort}`);
  };

  // 画像クリック処理
  const handleImageClick = (image: ImageData) => {
    console.log(`画像クリック: ${image.title}`);
  };

  // スケルトンカードコンポーネント
  const SkeletonCard: React.FC = () => (
    <div className="masonry-card">
      <div className="masonry-card-image skeleton-image"></div>
    </div>
  );

  // 画像カードコンポーネント
  const ImageCard: React.FC<{ image: ImageData }> = ({ image }) => {
    const [imageLoaded, setImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);

    const handleImageLoad = () => {
      console.log(`画像読み込み成功: ${image.url}`);
      setImageLoaded(true);
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      console.error(`画像読み込み失敗: ${image.url}`);
      const target = e.target as HTMLImageElement;
      console.error(`詳細エラー:`, target.src);
      setImageError(true);
    };

    return (
      <article 
        className="masonry-card"
        tabIndex={0}
        role="button"
        aria-label={`${image.title} - ¥${image.price.toLocaleString()}`}
        onClick={() => handleImageClick(image)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleImageClick(image);
          }
        }}
      >
        <div className="masonry-card-image">
          {imageError ? (
            <div className="error-image">
              画像を読み込めませんでした
              <br />
              <small>{image.url}</small>
            </div>
          ) : (
            <img
              src={image.url}
              alt={image.title}
              loading="lazy"
              decoding="async"
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{
                opacity: imageLoaded ? 1 : 0,
                transition: 'opacity 0.3s ease-in-out'
              }}
            />
          )}
          <div className="masonry-card-overlay">
            <div className="masonry-card-title">{image.title}</div>
            <div className="masonry-card-meta">
              <div className="masonry-card-rating">
                <span>★</span>
                <span>{image.rating}</span>
              </div>
              <div className="masonry-card-price">¥{image.price.toLocaleString()}</div>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className={`masonry-gallery-container ${className}`}>
      {/* ヘッダー */}
      <header className="masonry-header">
        <div className="masonry-header-content">
          <div className="masonry-logo">画像ギャラリー</div>
          <div className="masonry-filters">
            <div className="masonry-filter-group">
              <span>カテゴリ:</span>
              <button 
                className={`masonry-filter-btn ${activeFilter === 'all' ? 'active' : ''}`}
                onClick={() => handleFilterChange('all')}
              >
                すべて
              </button>
              <button 
                className={`masonry-filter-btn ${activeFilter === 'people' ? 'active' : ''}`}
                onClick={() => handleFilterChange('people')}
              >
                人物
              </button>
              <button 
                className={`masonry-filter-btn ${activeFilter === 'nature' ? 'active' : ''}`}
                onClick={() => handleFilterChange('nature')}
              >
                風景
              </button>
              <button 
                className={`masonry-filter-btn ${activeFilter === 'animals' ? 'active' : ''}`}
                onClick={() => handleFilterChange('animals')}
              >
                動物
              </button>
            </div>
            <div className="masonry-filter-group">
              <span>並び替え:</span>
              <button 
                className={`masonry-sort-btn ${activeSort === 'relevance' ? 'active' : ''}`}
                onClick={() => handleSortChange('relevance')}
              >
                関連度
              </button>
              <button 
                className={`masonry-sort-btn ${activeSort === 'newest' ? 'active' : ''}`}
                onClick={() => handleSortChange('newest')}
              >
                新着
              </button>
              <button 
                className={`masonry-sort-btn ${activeSort === 'popular' ? 'active' : ''}`}
                onClick={() => handleSortChange('popular')}
              >
                人気
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="masonry-container">
        <div className="masonry-gallery">
          {isLoading ? (
            // スケルトン表示
            Array.from({ length: 8 }, (_, i) => <SkeletonCard key={i} />)
          ) : (
            // 画像カード表示
            images.map((image) => (
              <ImageCard key={image.id} image={image} />
            ))
          )}
        </div>
        {isLoading && (
          <div className="masonry-loading-message">読み込み中...</div>
        )}
      </main>
    </div>
  );
};

export default MasonryGallery;